"use client";
import {
  Backdrop,
  Box,
  Card,
  CardActionArea,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchNormalIcon from "@/app/_components/Icons/SearchNormalIcon";
import React, { useCallback, useState } from "react";
import { useDebounce } from "use-debounce";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGqlClient } from "@/Providers/GQLProvider";
import NextLink from "next/link";
import Image from "next/image";
import SearchContentLoading from "@/app/(main)/_components/SearchContentLoading";
import { useBreakPoints } from "@/lib/useBreakPoints";
import { CloseIcon } from "next/dist/client/components/react-dev-overlay/internal/icons/CloseIcon";
import { useRouter } from "next/navigation";

export default function SearchContent(props: { onSelect: () => void }) {
  const { onSelect } = props;
  const { mdAndDown } = useBreakPoints();
  const router = useRouter();
  const [value, setValue] = useState("");
  const [deferredValue] = useDebounce(value, 700);
  const gqlClient = useGqlClient();
  const { data, isLoading, isFetching } = useQuery({
    placeholderData: keepPreviousData,
    staleTime: 12000,
    queryKey: [gqlClient.searchTerm.name, deferredValue],
    queryFn() {
      return gqlClient.searchTerm({ input: { query: deferredValue } });
    },
    enabled: Boolean(deferredValue),
  });
  const pushSearch = useCallback((text: string) => {
    if (text) {
      router.push(`/products?search=${text}`);
      onSelect();
    }
  }, []);
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderRadius: 4 }}>
        <Box sx={{ p: { xs: 1, md: 0 }, display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={onSelect}
            sx={(theme) => ({
              border: 1,
              display: { md: "none", xs: "flex" },
              borderColor: theme.palette.grey["300"],
            })}
          >
            <Icon>
              <CloseIcon />
            </Icon>
          </IconButton>
          <TextField
            variant={mdAndDown ? "outlined" : "standard"}
            color={"secondary"}
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={(theme) => ({
              "& .MuiInputBase-root": {
                borderRadius: "500px !important",
                height: 56,
                [theme.breakpoints.down("md")]: {
                  height: 42,
                },
              },
              "& fieldset": {
                borderRadius: "500px !important",
              },
            })}
            autoFocus={true}
            placeholder={"دنبال چی بگردم ؟"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                pushSearch(value);
              }
            }}
            InputProps={{
              sx: { borderRadius: "500px !important" },
              endAdornment: (
                <InputAdornment
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 40,
                    // pointerEvents: "none",
                  }}
                  position="start"
                >
                  <IconButton
                    onClick={() => {
                      pushSearch(value);
                    }}
                  >
                    <SearchNormalIcon />
                  </IconButton>
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 5,
                    pointerEvents: "none",
                  }}
                  position="start"
                >
                  {/* <IconButton onClick={() => {}}>
                    <SearchNormalIcon />
                  </IconButton> */}
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={() => {
              pushSearch(value);
            }}
            sx={(theme) => ({
              border: 1,
              display: { md: "none", xs: "flex" },
              borderColor: theme.palette.grey["300"],
            })}
          >
            <Icon>
              <SearchNormalIcon />
            </Icon>
          </IconButton>
        </Box>

        {data && deferredValue ? (
          <Box sx={{ p: 1, position: "relative" }}>
            <Backdrop className={"skeleton"} sx={(theme) => ({ color: "#fff" })} open={isFetching}></Backdrop>

            <Stack spacing={2}>
              <Stack spacing={1}>
                {data.searchTerm.products.map((product) => {
                  const link = `/products/${product.code}/${product.title.replaceAll(" ", "-")}`;
                  return (
                    <Card sx={{ borderRadius: 2 }} elevation={0} key={product.id}>
                      <CardActionArea disableRipple={true} component={NextLink} href={link} onClick={onSelect}>
                        <Stack spacing={1} direction={"row"}>
                          {product.mainImage && (
                            <Card
                              elevation={0}
                              variant={"outlined"}
                              sx={{
                                borderRadius: 3,
                                width: 48,
                                minWidth: 48,
                                maxHeight: 48,
                                aspectRatio: 1,
                                position: "relative",
                              }}
                            >
                              <Image
                                src={product.mainImage.address}
                                fill
                                alt={product.mainImage.alt || product.title}
                                style={{ objectFit: "cover" }}
                              />
                            </Card>
                          )}
                          <Stack
                            width={"100%"}
                            sx={{
                              overflow: "hidden", // Ensures content doesn't overflow the stack
                              textOverflow: "ellipsis", // Adds ellipsis where applicable
                              whiteSpace: "nowrap", // Prevents wrapping within the stack
                            }}
                          >
                            <Typography
                              variant={"subtitle2"}
                              noWrap
                              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {product.title}
                            </Typography>
                            <Typography variant={"body2"} className={""} sx={{ color: "grey.500" }}>
                              {" "}
                              در دسته <strong>{product.category.title}</strong>
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Stack>
              {data.searchTerm.categories.length > 0 && (
                <>
                  <Divider sx={{ borderBottomColor: "grey.100" }} />
                  <Stack spacing={1} sx={{ px: { md: 0, xs: 1 } }}>
                    {data.searchTerm.categories.map((category) => {
                      const link = `/categories/${category.path}`;
                      return (
                        <Card sx={{ borderRadius: 2 }} elevation={0} key={category.id}>
                          <CardActionArea disableRipple={true} component={NextLink} href={link} onClick={onSelect}>
                            <Stack spacing={1} direction={"row"}>
                              <Stack
                                width={"100%"}
                                spacing={0.5}
                                sx={{
                                  overflow: "hidden", // Ensures content doesn't overflow the stack
                                  textOverflow: "ellipsis", // Adds ellipsis where applicable
                                  whiteSpace: "nowrap", // Prevents wrapping within the stack
                                }}
                              >
                                <Typography
                                  variant={"body2"}
                                  noWrap
                                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", gap: 1 }}
                                >
                                  <Icon fontSize={"small"}>
                                    <SearchNormalIcon />
                                  </Icon>
                                  <strong>{category.title}</strong>
                                </Typography>
                                <Typography variant={"body2"} className={""} sx={{ color: "grey.700" }}>
                                  در دسته{" "}
                                  <Typography variant={"body2"} component={"span"} sx={{ color: "secondary.main" }}>
                                    {category.parentTitle}
                                  </Typography>
                                </Typography>
                              </Stack>
                            </Stack>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        ) : isLoading ? (
          <SearchContentLoading />
        ) : (
          <></>
        )}
      </Box>
    </>
  );
}
